import React, { memo, useCallback, useEffect, useState } from "react";
import {
    Badge,
    Grid,
    IconButton,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TextField,
    Typography,
    Tooltip,
} from "@mzc-pdc/ui";
import { useTranslation } from "react-i18next";
import { IconRefresh, IconSearch } from "@mzc-cloudplex/icons";
import { NoSearchResultsIcon } from "../no-search-results-icon";
import { useInView } from "react-intersection-observer";
import SelectCollectionsSkeleton from "@routes/asset/select-collections-skeleton";
import { useDispatch, useSelector } from "react-redux";
import { clearModalList, getCollectionsModal } from "@modules/collections";
import SelectDialogWrapper from "@features/dialog/select-dialog-wrapper";
import TableCard from "@components_v2/card/table-card";
import CollectionThumbnail from "@features/thumbnail/collection-thumbnail";
import theme from "@styles/theme";
import BadgeCollection from "@components_v2/badge-collection/badge-collection";
import i18n from "@lib/i18n";

export const DialogSelectCollection = ({
    open,
    disableDialog = false,
    title,
    selectLabel,
    placeholder,
    onSubmit,
    actionPending,
    onClose,
    getValueByColumn,
    isDisabledCheckbox = false,
    onChangeCollection,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const collections = useSelector(({ collections }) => collections.create);

    const [searchValue, setSearchValue] = useState({ searchedValue: null, debouncedValue: null });
    const [selectedOption, setSelectedOption] = useState();

    const columns = [
        {
            id: "name",
            label: "Name",
            style: { custom: { width: `calc(100% - 48px)` } },
            disabled: true,
            checked: true,
            sortable: false,
            useInAutocomplete: true,
        },
        {
            id: "count",
            label: "Assets Count",
            style: { custom: { width: 72 } },
            disabled: false,
            checked: true,
            sortable: false,
            useInAutocomplete: true,
        },
    ];

    const [targetRef, inView] = useInView();
    const DEFAULT_LIMIT = 20;
    const DEFAULT_SKELETON_CNT = 3;

    const getSkeletonCardLength = useCallback(() => {
        if (!collections.data || collections.data?.length === 0) return DEFAULT_SKELETON_CNT;
        return 3;
    }, [collections?.pending]);

    const onHandleSearchCollection = (value) => {
        if (value !== searchValue.searchedValue) {
            setSearchValue({ ...searchValue, searchedValue: value });
        }
    };

    const onHandleCheckClick = (option) => {
        setSelectedOption(option);
        if (onChangeCollection) onChangeCollection([{ id: option.id, name: option.name }]);
    };

    const onClickRefresh = () => {
        const params = { name: searchValue.debouncedValue, limit: DEFAULT_LIMIT, offset: 0 };
        setSelectedOption();
        getCollections(params);
    };

    const getCollections = useCallback(
        (params) => {
            dispatch(clearModalList());
            dispatch(getCollectionsModal(params));
        },
        [dispatch],
    );

    const onFetchCollection = useCallback(
        (params) => {
            if (!collections?.nextToken || collections.pending) return;
            dispatch(getCollectionsModal({ ...params, nextToken: collections.nextToken }));
        },
        [collections?.nextToken, collections.pending, dispatch],
    );

    const onHandleSubmit = useCallback(() => {
        onSubmit(selectedOption);
    }, [selectedOption]);

    const getCollectionTableValueByColumn = useCallback((columnId, option) => {
        if (columnId === "name")
            return (
                <TableCard
                    thumbnail={<CollectionThumbnail collection={option} size={"small"} variant={"table"} />}
                    name={{
                        name: (
                            <Typography variant={"body1"} component={"span"}>
                                {option?.name}
                            </Typography>
                        ),
                    }}
                />
            );
        if (columnId === "count")
            return (
                <Grid item>
                    <BadgeCollection totalCount={option?.assetTotalCount} />
                </Grid>
            );
        return null;
    }, []);

    useEffect(() => {
        if (inView) {
            onFetchCollection({ name: searchValue.debouncedValue });
        }
    }, [inView, searchValue.debouncedValue, onFetchCollection]);

    useEffect(() => {
        const searchedValue = searchValue.searchedValue;
        if (searchedValue === null) return;
        const debounce = setTimeout(() => {
            setSearchValue((prev) => ({ ...prev, debouncedValue: searchedValue }));
        }, 300);
        return () => clearTimeout(debounce);
    }, [searchValue.searchedValue]);

    useEffect(() => {
        const params = { name: searchValue.debouncedValue, limit: DEFAULT_LIMIT, offset: 0 };
        getCollections(params);
    }, [searchValue.debouncedValue]);

    return (
        <SelectDialogWrapper
            t={t}
            open={open}
            disableDialog={disableDialog}
            title={title}
            isSubmitButtonDisabled={actionPending}
            onHandleSubmit={onHandleSubmit}
            selectLabel={selectLabel}
            onClose={onClose}
        >
            <Grid container flexDirection={"row"}>
                <Grid item flex={1}>
                    <TextField
                        sx={{ width: "400px" }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <IconSearch size={12} />
                                </InputAdornment>
                            ),
                        }}
                        type="search"
                        variant="outlined"
                        placeholder={placeholder}
                        onChange={(e) => {
                            e.stopPropagation();
                            const replacedString = e.target.value.replace(/[\n\s\r\t]*/g, "");
                            onHandleSearchCollection(replacedString);
                        }}
                    />
                </Grid>
                <Grid item>
                    <Tooltip title={i18n.t(`common::label::Refresh`, `Refresh`)}>
                        <IconButton circled variant={"text"} color={"inherit"} size={`medium`} onClick={onClickRefresh}>
                            <IconRefresh size={16} />
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>
            <Grid container mt={2} minWidth={552} height={300} sx={{ overFlow: "none" }}>
                <TableContainer sx={{ overflowY: "auto", height: "100%" }}>
                    {!collections?.pending && !collections.data?.length && (
                        <NoSearchResultsIcon
                            title={`None of collection matched this search. {{line}} Try another search.`}
                        />
                    )}
                    <Table stickyHeader shadowHeader borderHeader sx={{ tableLayout: "fixed" }}>
                        <TableBody>
                            {collections.data?.map((option, index) => (
                                <TableRow
                                    key={option.id}
                                    id={option.id}
                                    hover
                                    role="checkbox"
                                    tabIndex={-1}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onHandleCheckClick(option);
                                    }}
                                    selected={selectedOption?.id === option.id}
                                >
                                    {!isDisabledCheckbox && (
                                        <TableCell
                                            style={{ paddingRight: "0px", width: "16px" }}
                                            padding="checkbox"
                                        ></TableCell>
                                    )}
                                    {columns
                                        .filter((item) => item.useInAutocomplete)
                                        .map((column) => (
                                            <TableCell
                                                key={column.id}
                                                sx={{
                                                    ...column.style.custom,
                                                    borderBottom:
                                                        index === collections.data.length - 1 ? "none" : undefined,
                                                    pl: !isDisabledCheckbox && column.id === "name" ? "0px" : undefined,
                                                }}
                                            >
                                                {getValueByColumn
                                                    ? getValueByColumn(column.id, option)
                                                    : getCollectionTableValueByColumn(column.id, option)}
                                            </TableCell>
                                        ))}
                                </TableRow>
                            ))}
                            {collections?.pending &&
                                new Array(getSkeletonCardLength()).fill("").map((_, i) => (
                                    <TableRow key={i}>
                                        <SelectCollectionsSkeleton
                                            columns={columns.filter((item) => item.useInAutocomplete)}
                                            isDisabledCheckbox={true}
                                        />
                                    </TableRow>
                                ))}
                            <div ref={targetRef} />
                        </TableBody>
                    </Table>
                </TableContainer>
                <Typography variant={`body4Regular`} />
            </Grid>
        </SelectDialogWrapper>
    );
};

export default memo(DialogSelectCollection);
